/**
 * Validates a post-sign-in return path. Accepts only canonical same-origin
 * paths (query + fragment preserved); rejects network paths, backslashes and
 * control characters. Returns `fallback` for anything else.
 */
export function oauthReturnPath(value: unknown, fallback = "/"): string {
  if (
    typeof value !== "string" ||
    !value.startsWith("/") ||
    value.startsWith("//") ||
    // eslint-disable-next-line no-control-regex
    /[\\\u0000-\u001f\u007f]/.test(value)
  ) {
    return fallback;
  }
  const origin = "https://app.invalid";
  try {
    const target = new URL(value, origin);
    if (target.origin !== origin || target.pathname.startsWith("//")) return fallback;
    return target.pathname + target.search + target.hash;
  } catch {
    return fallback;
  }
}

const PENDING_KEY = "excerptus.pendingOAuthReturn";
const PENDING_TTL_MS = 30 * 60 * 1000;

/**
 * Remembers an in-progress Claude.ai consent URL so a user who has to sign up
 * (checkout + magic link) still lands back on the consent screen afterwards.
 */
export function rememberPendingOAuthReturn(path: string) {
  const safe = oauthReturnPath(path, "");
  if (!safe.startsWith("/.lovable/oauth/consent")) return;
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify({ path: safe, at: Date.now() }));
  } catch {
    /* storage unavailable */
  }
}

export function takePendingOAuthReturn(): string | null {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    localStorage.removeItem(PENDING_KEY);
    const { path, at } = JSON.parse(raw) as { path?: string; at?: number };
    if (!path || !at || Date.now() - at > PENDING_TTL_MS) return null;
    const safe = oauthReturnPath(path, "");
    return safe.startsWith("/.lovable/oauth/consent") ? safe : null;
  } catch {
    return null;
  }
}
